/**
 * Командный раздел «Документы» (documents, team-portal): реестр документов ВСЕХ клиентов с колонкой
 * владельца. Фильтры: клиент (uuid) и тип документа — серверные (команда видит всех; контур большой).
 * Скачивание доступно команде в пределах её доступа (бэкенд скоупит).
 */
import { useEffect, useState } from 'react'
import { FileText } from 'lucide-react'
import { teamApi, type ClientSummary, type DocumentDTO } from '@/lib/api'
import { useSeesAllClients } from '@/team/roles'
import { getErrorMessage } from '@/lib/errors'
import { DOCUMENT_TYPES } from '@/lib/orders'
import { PAGE_SIZE, pageCount } from '@/lib/pagination'
import { Pagination } from '@/design-system/components/data/pagination'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Panel } from '@/design-system/components/layout/panel'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'

const ALL = 'all'

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function TeamDocumentsPage() {
  const seesAll = useSeesAllClients()
  const [items, setItems] = useState<DocumentDTO[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [clients, setClients] = useState<ClientSummary[]>([])
  const [client, setClient] = useState(ALL)
  const [type, setType] = useState(ALL)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Смена любого фильтра открывает первую страницу (эталон `/team/orders`) — сброс прямо
  // в обработчике, иначе на смену фильтра ушёл бы лишний запрос со старым номером страницы.
  function changeFilter(apply: () => void) {
    apply()
    setPage(1)
  }

  useEffect(() => {
    const controller = new AbortController()
    teamApi
      .listClients(undefined, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setClients(res.items)
      })
      .catch(() => undefined)
    return () => controller.abort()
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    teamApi
      .listDocuments(
        {
          client: client === ALL ? undefined : client,
          type: type === ALL ? undefined : type,
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
        },
        controller.signal,
      )
      .then((res) => {
        if (controller.signal.aborted) return
        // Страница «уехала» за пределы результатов (фильтр сузил реестр) — на первую.
        if (res.items.length === 0 && page > 1) {
          setPage(1)
          return
        }
        setItems(res.items)
        setTotal(res.total)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить документы'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [client, type, page])

  return (
    <div>
      <PageHead
        title="Документы"
        subtitle={seesAll ? 'Реестр документов всех клиентов' : 'Документы ваших клиентов и заказов'}
      />

      <Toolbar aria-label="Фильтры документов">
        <Select value={client} onValueChange={(v) => changeFilter(() => setClient(v))}>
          <SelectTrigger className="w-56" aria-label="Клиент">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Все клиенты</SelectItem>
            {clients.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={(v) => changeFilter(() => setType(v))}>
          <SelectTrigger className="w-56" aria-label="Тип документа">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Все типы</SelectItem>
            {DOCUMENT_TYPES.map((t) => (
              <SelectItem key={t.code} value={t.code}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Toolbar>

      <Panel>
        {error ? (
          <Alert variant="danger" className="m-4">
            {error}
          </Alert>
        ) : loading ? (
          <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
        ) : items.length === 0 ? (
          <EmptyState icon={FileText} title="Документов нет" description="По выбранным фильтрам пусто" />
        ) : (
          <DataTable>
            <DataTableHead>
              <DataTableRow>
                <DataTableHeaderCell>Документ</DataTableHeaderCell>
                <DataTableHeaderCell>Тип</DataTableHeaderCell>
                <DataTableHeaderCell>Владелец</DataTableHeaderCell>
                <DataTableHeaderCell>Дата</DataTableHeaderCell>
                <DataTableHeaderCell>Действие</DataTableHeaderCell>
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {items.map((d) => (
                <DataTableRow key={d.id}>
                  <DataTableCell className="font-medium">{d.title}</DataTableCell>
                  <DataTableCell className="text-txt-secondary">{d.typeName}</DataTableCell>
                  <DataTableCell className="text-txt-secondary">{d.ownerName ?? '—'}</DataTableCell>
                  <DataTableCell className="text-txt-secondary">{formatDate(d.date)}</DataTableCell>
                  <DataTableCell>
                    <Button asChild size="sm" variant="secondary">
                      <a href={d.downloadUrl} download>
                        Скачать
                      </a>
                    </Button>
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        )}
      </Panel>
      <Pagination page={page} totalPages={pageCount(total)} onChange={setPage} />
    </div>
  )
}
